import React from 'react';

import QrCodeRoundedIcon from '@mui/icons-material/QrCodeRounded';

import classNames from 'classnames';
import { observer } from 'mobx-react';
import { Button } from '@infoods/lifebase-ui';

import classes from './BannerWidget.module.scss';
import imgBannerNutrition from '../../../shared/assets/image/banners/img-banner-nutrition.png';
import imgBannerScanIq from '../../../shared/assets/image/banners/img-banner-scan-iq.png';
import { SOLUTION_TYPE } from '../../../shared/constants/user-info/solutionType';
import { loggedInUserDataStore } from '../../../shared/stores/loggedInUserDataStore';
import { getUserName } from '../../../shared/utils/userInfoUtils';
import { getHumanizeSolutionType } from '../../../shared/utils/userInfoUtils/getHumanizeSolutionType/getHumanizeSolutionType';
import {
  nutritionRegistrationService,
  RegistrationQrCodeModal,
  RegistrationService,
  scanIqRegistrationService,
} from '../../../auth-management';
import { openNewTab } from '../../../shared/utils/windowUtils';
import { useModalToggle } from '../../../shared/hooks/useModalToggle';
import { SearchInput } from '../../../shared/components/form-components/autocomplete-components/SearchInput/SearchInput';
import { OptionWithDescription } from '../../../shared/components/form-components';
import { useSearchCustomer } from '../../hooks/useSearchCustomer';
import { SearchCustomerResult } from '../../interfaces/SearchCustomerResult';
import { getValueOrDash } from '../../../shared/utils/stringUtils/getValueOrDash/getValueOrDash';

type BennerSolutionType = typeof SOLUTION_TYPE.NUTRITION | typeof SOLUTION_TYPE.SCAN_IQ;

type BannerWidgetProps = {
  solutionType: BennerSolutionType;
};

const registrationServiceMap: Record<BennerSolutionType, RegistrationService> = {
  [SOLUTION_TYPE.SCAN_IQ]: scanIqRegistrationService,
  [SOLUTION_TYPE.NUTRITION]: nutritionRegistrationService,
};

const bannerImageMap: Record<BennerSolutionType, string> = {
  [SOLUTION_TYPE.NUTRITION]: imgBannerNutrition,
  [SOLUTION_TYPE.SCAN_IQ]: imgBannerScanIq,
};

const BannerWidget = observer(({ solutionType }: BannerWidgetProps) => {
  const [isQrCodeModalOpen, toggleQrCodeModalOpen] = useModalToggle(false);

  const { loggedInUser, getEntityId } = loggedInUserDataStore;

  const entityId = getEntityId();

  const { inputValue, options, loading, onInputChange, handleSelectCustomer } =
    useSearchCustomer({
      solutionType,
    });

  const registrationService = registrationServiceMap[solutionType];

  const userName = getUserName(loggedInUser);
  const containerClassName = classNames(classes.container, {
    [classes.nutrition]: solutionType === SOLUTION_TYPE.NUTRITION,
    [classes.scanIq]: solutionType === SOLUTION_TYPE.SCAN_IQ,
  });
  const bannerImage = bannerImageMap[solutionType];

  const handleOpenRegistrationForm = async () => {
    const { url } = await registrationService.getRegistrationLink(entityId);

    openNewTab(url);
  };

  const renderOptionLabel = (option: SearchCustomerResult) => {
    const label =
      option.firstname || option.lastname
        ? `${getValueOrDash(option.firstname)} ${getValueOrDash(option.lastname)}`
        : 'No name provided';

    const phone = option.phone || 'No phone provided';
    const email = option.email || 'no email provided';

    return (
      <div className={classes.optionContainer}>
        <OptionWithDescription label={label} description={`${phone}, ${email}`} />
      </div>
    );
  };

  return (
    <>
      <div className={containerClassName}>
        <div className={classes.content}>
          <div>
            <p className={classes.title}>Welcome back,</p>
            <p className={classes.userName}>{`${userName}!`}</p>
          </div>
          <div>
            <SearchInput
              className={classes.searchBar}
              options={options}
              inputValue={inputValue}
              onInputChange={onInputChange}
              onSelectOption={handleSelectCustomer}
              loading={loading}
              getOptionLabel={(option) => `${option.firstname} ${option.lastname}`}
              getOptionKey={(option) => option.customerId}
              isOptionEqualToValue={(option, value) =>
                option.customerId === value.customerId
              }
              renderOptionLabel={renderOptionLabel}
              placeholder="Search customer by name, phone number, email"
              variant="outlined"
              size="small"
            />
            <div className={classes.buttonsContainer}>
              <Button
                variant="contained"
                size="small"
                onClick={handleOpenRegistrationForm}
              >
                + Add a {getHumanizeSolutionType(solutionType)} User
              </Button>
              <Button
                className="wightBg"
                variant="outlined"
                size="small"
                startIcon={<QrCodeRoundedIcon />}
                onClick={toggleQrCodeModalOpen}
              >
                QR Code
              </Button>
            </div>
          </div>
        </div>
        <div className={classes.imageContainer}>
          <img src={bannerImage} alt="banner-image" />
        </div>
      </div>
      {isQrCodeModalOpen && (
        <RegistrationQrCodeModal
          id={entityId}
          getRegistrationQrCode={registrationService.getRegistrationQrCode}
          onClose={toggleQrCodeModalOpen}
        />
      )}
    </>
  );
});

export { BannerWidget };
